import * as $ from 'jquery';

import * as CanvasTemplates from 'src/integration/canvas/templates';
import * as CanvasPreview from 'src/integration/canvas/ui/preview-new-experience';
import { throttle } from 'src/integration/canvas/ui/throttleUtil';
import * as CanvasUiUtil from 'src/integration/canvas/ui/util';
import { canManageFilesTool } from 'src/integration/canvas/ui/util';
import * as AllyIntegrationUtil from 'src/integration/util';
import * as NullUtil from 'src/shared/NullUtil';
import * as AllyConfig from 'src/shared/rest/Config';
import { FileReference } from 'src/shared/rest/domain/FileReference';

// The ids of the files currently on the page
let fileIds = [];

// Constants
const FILE_ID_COLLECTION_INTERVAL = 1000;
/**
 * Throttled version of collectFileIdsAndAddRelevantIcons
 */
const throttleCollectFileIds = throttle(collectFileIdsAndAddRelevantIcons, FILE_ID_COLLECTION_INTERVAL);

/**
 * Initialize the files tool integration if the files tool is currently active
 */
export function init() {
    collectFileIdsAndAddRelevantIcons();
    addAltFormatsButtonToPreviewToolbar();
}

/**
 * Add an accessible versions button to the Files tool toolbar. This button will
 * be shown and hidden depending on the current selection in the files tool
 * The mutation observer will readd the button because the mjority of the page is redrawn when resizing above/below
 * a threshold
 */
let filesToolbarObserver = null;

function addAltFormatsButtonToPreviewToolbar() {
    AllyIntegrationUtil.wait(
        function() {
            return $('div[style*="display: grid"]')[0] && $('#download-icon-button')[0];
        },
        function() {
            insertAllyIconToToolbar();

            // Disconnect any existing observer
            if (filesToolbarObserver) {
                filesToolbarObserver.disconnect();
            }

            filesToolbarObserver = new MutationObserver(() => {
                const $grid = $('div[style*="display: grid"]');
                if ($grid.length && $grid.find('div[style*="grid-area: Ally"]').length === 0) {
                    insertAllyIconToToolbar();
                }
            });
            filesToolbarObserver.observe(document.body, { 'childList': true, 'subtree': true });
        }
    );
}

/**
 * Insert the Ally icon into the Files tool toolbar. This icon will be used to access the accessible versions of files.
 */
function insertAllyIconToToolbar() {
    const $grid = $('div[style*="display: grid"]');
    let style = $grid.attr('style');
    if (style && !style.includes('Ally')) {
        style = style.replace(/"([^"]*?)close([^"]*?)"/, '"$1Ally close$2"');
        $grid.attr('style', style);
    }
    $grid.find('div[style*="grid-area: Ally"]').remove();
    const $allyDiv = $('<div style="grid-area: Ally;"></div>');
    $allyDiv.append(CanvasTemplates.apply('ally-files-accessible-versions-toolbar-new-files', {
        'ALLY_DOMAIN': AllyConfig.baseUrl()
    }));
    const $closeDiv = $grid.find('div[style*="grid-area: close"]');
    $allyDiv.insertBefore($closeDiv);
    initializeTooltipForPreviewButton();
}


/**
 * Initialize the tooltip for the accessible versions button
 */
function initializeTooltipForPreviewButton() {
    const $button = $('#ally-accessible-versions-toolbar-new');
    if ($button.length > 0 && !$button.data('ui-tooltip')) {
        $button.tooltip({
            'position': {
                'my': 'center bottom-5',
                'at': 'center top'
            },
            'classes': {
                'ui-tooltip': 'ui-corner-all bottom center'
            }
        });
    }
}


/**
 * Collect the ids of the files in the current tool and update the toolbar button with relevant icons.
 */
let lastFileInfos = {};
function collectFileIdsAndAddRelevantIcons() {
    const focusedElement = document.activeElement;
    const { collectedFileIds, selectedFileId } = getCollectedFileIds();
    updateFileInfos(collectedFileIds, () => {
        updateToolbarButton(selectedFileId);
    });

    CanvasPreview.pollPreview(
        $previewFrame =>
            $previewFrame.find('[data-testid="previous-button"]').length === 1 &&
            $previewFrame.find('[data-testid="next-button"]').length === 1,
        () => AllyIntegrationUtil.getQueryParameters(window.location.href).preview
    );

    restoreFocusIfNeeded(focusedElement);

    setTimeout(throttleCollectFileIds, FILE_ID_COLLECTION_INTERVAL);
}

/**
 * Collects the file ids from the current files tool page.
 * We basically go through avery a element with a data-testid attribute that contains a link to a file
 * we can then parse the file id from the link.
 * We also need to check if there is an iframe with a file preview,
 * in which case we can get the selected file id from the iframe src.
 * @returns {{collectedFileIds: *[], selectedFileId: null}}
 */
function getCollectedFileIds() {
    const collectedFileIds = [];
    let selectedFileId = null;
    $('a[data-testid]').each(function() {
        const fileId = CanvasUiUtil.getFileId(this.href);
        if (!fileId) return;

        collectedFileIds.push(fileId);
        const $iframe = $('iframe[src*="/file_preview"]');
        if (!$iframe.length) return;

        const src = $iframe.attr('src');
        const match = src.match(/\/files\/(\d+)\/file_preview/);
        if (match && match[1]) {
            selectedFileId = match[1];
        }
    });
    const previewFileId = AllyIntegrationUtil.getQueryParameters(window.location.href).preview;
    if (previewFileId) {
        collectedFileIds.push(previewFileId);
    }
    return { collectedFileIds, selectedFileId };
}

/**
 * Update the file information for the files in the current tool.
 * Then enhance the files tool with the Ally integration features.
 * @param collectedFileIds
 * @param callback not called in case no file information has changed
 */
function updateFileInfos(collectedFileIds, callback) {
    const fileReferences = collectedFileIds.map(fileId => new FileReference(fileId));
    const shouldUpdate = fileIds.toString() !== collectedFileIds.toString();

    function handleFileInfo(err) {
        if (err) {
            console.error(err);
            return;
        }
        if (shouldUpdate || fileInfoChanged()) {
            enhanceFilesTool();
            updateLastFileInfos();
        }
        callback();
    }

    if (shouldUpdate) {
        fileIds = collectedFileIds;
    }
    CanvasUiUtil.getFileInformation(fileReferences, handleFileInfo);
}

/**
 * Update the last file information cache with the current file information.
 */
function updateLastFileInfos() {
    const currentInfos = CanvasUiUtil.fileInformation();
    lastFileInfos = {};
    for (const id of fileIds) {
        lastFileInfos[id] = currentInfos[id] && JSON.parse(JSON.stringify(currentInfos[id]));
    }
}

/**
 * Check if the file information has changed since the last time it was checked.
 * @returns {boolean}
 */
function fileInfoChanged() {
    const currentInfos = CanvasUiUtil.fileInformation();
    for (const id of fileIds) {
        if (JSON.stringify(currentInfos[id]) !== JSON.stringify(lastFileInfos[id])) {
            return true;
        }
    }
    return false;
}

/**
 * Update the toolbar button for accessible versions based on the selected file id.
 * Only show the button if the selected file has available alternative formats.
 * @param selectedFileId
 */
function updateToolbarButton(selectedFileId) {
    const selectedFileReference = selectedFileId && new FileReference(selectedFileId);
    const selectedFileReferenceId = selectedFileReference && selectedFileReference.id();
    const selectedFileInfo = selectedFileReferenceId && CanvasUiUtil.fileInformation()[selectedFileReferenceId];
    if (selectedFileInfo && selectedFileInfo.availableAlternativeFormats.length > 0) {
        $('#ally-accessible-versions-toolbar-new')
            .attr('data-id', selectedFileId)
            .css({ 'display': 'flex' })
            .tooltip({
                'position': { 'my': 'center bottom-5', 'at': 'center top' },
                'classes': { 'ui-tooltip': 'ui-corner-all bottom center' }
            });
    } else {
        $('#ally-accessible-versions-toolbar-new').css({ 'display': 'none' });
    }
}

/**
 * Restore focus to the previously focused element if it still exists in the DOM.
 * @param focusedElement
 */
function restoreFocusIfNeeded(focusedElement) {
    if (NullUtil.hasValue(focusedElement) && document.activeElement !== focusedElement) {
        const restoredElement = document.getElementById(focusedElement.id);
        if (NullUtil.hasValue(restoredElement)) {
            restoredElement.focus();
        }
    }
}

/**
 * Get the row for a file in the files tool table based on the file id.
 * @param fileId
 * @param $row
 * @returns {*}
 */
function getRow(fileId) {
    let $row = null;
    $('[data-testid="table-row"]').each(function () {
        const $nameCell = $(this).find('td[data-testid="table-cell-name"]');
        const $link = $nameCell.find('a[href]');
        if ($link.length && $link.attr('href').includes(fileId)) {
            $row = $(this);
            return false;
        }
    });
    return $row;
}

/**
 * Inject Ally functionality into the files tool
 */
let enhancedFilesToolObserver = null; // Keep a record so we can disconnect it later
/**
 * Main entry: Enhances the Files tool with Ally features.
 */
function enhanceFilesTool() {
    const atLeastOneFileHasAllyScore = hasAnyFileWithAllyScore();

    if (canManageFilesTool() && atLeastOneFileHasAllyScore) {
        addAccessibilityColumnToTable();
    }

    const indexToFileId = collectIndexToFileIdAndEnhanceRows();

    setupEnhancedFilesToolObserver(indexToFileId);
}

/**
 * Checks if any file has an Ally score.
 * @returns {boolean}
 */
function hasAnyFileWithAllyScore() {
    let found = false;
    $.each(CanvasUiUtil.fileInformation(), function(fileId, fileInfo) {
        if (AllyIntegrationUtil.hasScore(fileInfo)) {
            found = true;
            return false; // Break out of the loop if we found at least one file with an Ally score
        }
    });
    return found;
}

/**
 * Adds the accessibility column heading and cells to the table.
 */
function addAccessibilityColumnToTable() {
    addFilesToolAccessibilityHeading();
    $('[data-testid="table-row"]').each(function() {
        const $row = $(this);
        $row.find('.ally-accessibility-indicator').remove();
        const $sizeCell = $row.find('[data-testid="table-cell-size"]');
        $sizeCell.after(CanvasTemplates.apply('ally-files-accessibility-cell-new-files'));
    });
}

/**
 * Collects index-to-fileId mapping and enhances each file row (score, seizure, etc).
 * @returns {Object} indexToFileId
 */
function collectIndexToFileIdAndEnhanceRows() {
    const indexToFileId = {};
    $.each(CanvasUiUtil.fileInformation(), function(fileReferenceId, file) {
        const fileId = file.id;
        const $row = getRow(fileId);
        if (!$row || !$row.length) return;

        addIndexToFileIdMapping($row, fileId, file, indexToFileId);
        addScoreIndicatorIfNeeded($row, file);
        addSeizureIndicatorIfNeeded($row, file);
    });
    return indexToFileId;
}

/**
 * Adds index-to-fileId mapping for dropdown insertion.
 */
function addIndexToFileIdMapping($row, fileId, file, indexToFileId) {
    const $actionSpan = $row.find('span[data-position^="Menu___"]');
    const positionAttr = $actionSpan.attr('data-position');
    if (!positionAttr) return;
    const index = positionAttr.split('___')[1];
    if (!fileId) return;
    indexToFileId[index] = {
        'fileId': fileId,
        'availableAlternativeFormats': file.availableAlternativeFormats.length
    };
}

/**
 * Adds the Ally score indicator to the row if the user can manage files.
 */
function addScoreIndicatorIfNeeded($row, file) {
    if (AllyIntegrationUtil.hasScore(file) && canManageFilesTool()) {
        const $scoreIndicator = AllyIntegrationUtil.createScoreIndicator(file);
        $('.ally-accessibility-indicator', $row).append($scoreIndicator);
        $('.ally-accessibility-indicator-not-available', $row).remove();
    }
}

/**
 * Adds the seizure risk indicator to the row if needed.
 */
function addSeizureIndicatorIfNeeded($row, file) {
    $row.find('.ally-files-seizure').remove();
    if (file.seizureRisk) {
        const $img = $row.find('[data-testid="table-cell-name"] img').first();
        const imgClass = $img.attr('class');
        const $indicator = $(CanvasTemplates.apply('ally-files-seizure-indicator-new-files', {
            'ALLY_DOMAIN': AllyConfig.baseUrl()
        }));
        if (imgClass) {
            $indicator.addClass(imgClass);
        }
        $img.replaceWith($indicator);
    }
}

/**
 * Sets up the MutationObserver to insert Ally dropdowns in action menus.
 */
function setupEnhancedFilesToolObserver(indexToFileId) {
    if (enhancedFilesToolObserver) {
        enhancedFilesToolObserver.disconnect();
    }

    enhancedFilesToolObserver = new MutationObserver(function(mutationsList) {
        mutationsList.forEach(mutation => {
            $(mutation.addedNodes).each(function() {
                const $span = $(this).closest('span[data-position-content]');
                if (!$span.length) return;

                const positionContent = $span.attr('data-position-content');
                const index = positionContent && positionContent.split('___')[1];

                if (!index || !indexToFileId[index]) return;

                if (indexToFileId[index].availableAlternativeFormats <= 0) return;

                const fileId = indexToFileId[index].fileId;
                let $btn = $span.find('[data-testid="action-menu-button-Rename"]');
                if (!$btn.length) {
                    $btn = $span.find('[data-testid="action-menu-button-Download"]');
                }
                insertAltFormatsLauncherToActionsDropdown(enhancedFilesToolObserver, $btn, fileId);
            });
        });
    });

    enhancedFilesToolObserver.observe(document.body, { 'childList': true, 'subtree': true });
}

function insertAltFormatsLauncherToActionsDropdown(observer, $referenceButton, fileId) {
    // Disconnect observer while modifying DOM
    observer.disconnect();

    const $allyActionButton = $(CanvasTemplates.apply('ally-files-accessible-versions-dropdown-new-files', { 'fileId': fileId }));

    // Copy button's class if needed
    const previousClass = $referenceButton.attr('class');
    if (previousClass) {
        $allyActionButton.addClass(previousClass);
    }
    const $separator = $('<div role="presentation" class="css-1e1j689-menuItemSeparator"></div>');

    // Insert action and separator before the button
    $referenceButton.before($allyActionButton, $separator);

    // handle hover state for the dropdown
    // Assuming menu items have a class 'menu-item' and hover state is 'hover'
    const menuItems = document.querySelectorAll('[data-testid^="action-menu-button-"]');

    function handleMouseEnter(e) {
        menuItems.forEach(item => item.classList.remove('hover'));
        e.currentTarget.classList.add('hover');
    }

    function handleMouseLeave(e) {
        e.currentTarget.classList.remove('hover');
    }

    // Attach listeners to all menu items (including dynamically inserted ones)
    menuItems.forEach(item => {
        item.removeEventListener('mouseenter', handleMouseEnter);
        item.removeEventListener('mouseleave', handleMouseLeave);
        item.addEventListener('mouseenter', handleMouseEnter);
        item.addEventListener('mouseleave', handleMouseLeave);
    });

    // Reconnect observer to continue observing changes
    observer.observe(document.body, { 'childList': true, 'subtree': true });
}

/**
 * Add an accessibility column to the Files tool list header. This heading will be used for a column that shows an
 * accessibility indicator for each file
 */
function addFilesToolAccessibilityHeading() {
    const $table = $('table[data-testid="files-table"]');
    const $thead = $table.find('thead');
    const $sizeHeader = $thead.find('th[data-testid="size"]');

    // Early exit if the accessibility column header already exists
    if ($sizeHeader.next('th.ally-accessibility-column-header').length !== 0) return;

    // Insert new header cell after the size column
    $sizeHeader.after(
        $('<th>')
            .addClass('ally-accessibility-column-header')
            .text('Accessibility')
    );
    // Insert new cell in each body row
    $table.find('tbody tr').each(function() {
        const $sizeCell = $(this).find('td').eq($sizeHeader.index());
        $sizeCell.after(
            $('<td>')
                .addClass('ally-accessibility-indicator')
        );
    });
}
